import { listProjects } from "@/lib/prometheusProjectsStore";

const INDEX_KEY = "prometheus_chat_index_v1";
const GENERAL_ID = "general";

export type ChatIndexEntry = {
  id: string;
  title: string;
  projectId?: string;
  updatedAt: string;
};

function readIndex(): ChatIndexEntry[] {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeIndex(entries: ChatIndexEntry[]) {
  localStorage.setItem(INDEX_KEY, JSON.stringify(entries));
}

function projectChatStorageKey(projectId: string) {
  return `prometheus_chat_proj_${projectId}`;
}

/** Sync index with existing project chat blobs + ensure general entry. */
export function listChatEntries(): ChatIndexEntry[] {
  const index = readIndex();
  const byId = new Map(index.map((e) => [e.id, e]));
  const projects = listProjects();

  if (!byId.has(GENERAL_ID)) {
    byId.set(GENERAL_ID, {
      id: GENERAL_ID,
      title: "Chat general",
      updatedAt: new Date(0).toISOString(),
    });
  }

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith("prometheus_chat_proj_")) continue;
      const projectId = key.slice("prometheus_chat_proj_".length);
      const chatId = `proj:${projectId}`;
      const project = projects.find((p) => p.id === projectId);
      const existing = byId.get(chatId);
      byId.set(chatId, {
        id: chatId,
        title: existing?.title || project?.title || `Proyecto ${projectId.slice(0, 8)}`,
        projectId,
        updatedAt: existing?.updatedAt || new Date().toISOString(),
      });
    }
  } catch {
    /* ignore */
  }

  return [...byId.values()].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

export function touchChatEntry(opts: {
  id: string;
  title?: string;
  projectId?: string;
}) {
  const entries = listChatEntries();
  const idx = entries.findIndex((e) => e.id === opts.id);
  const next: ChatIndexEntry = {
    id: opts.id,
    title: opts.title || entries[idx]?.title || "Chat",
    projectId: opts.projectId ?? entries[idx]?.projectId,
    updatedAt: new Date().toISOString(),
  };
  if (idx >= 0) entries[idx] = { ...entries[idx], ...next };
  else entries.unshift(next);
  writeIndex(entries);
  return next;
}

export function renameChatEntry(id: string, title: string) {
  const trimmed = title.trim();
  if (!trimmed) return;
  const entries = listChatEntries();
  const idx = entries.findIndex((e) => e.id === id);
  if (idx < 0) return;
  entries[idx] = { ...entries[idx], title: trimmed, updatedAt: new Date().toISOString() };
  writeIndex(entries);
}

export function deleteChatEntry(id: string) {
  if (id === GENERAL_ID) return false;
  const entries = listChatEntries().filter((e) => e.id !== id);
  writeIndex(entries);
  const projectId = id.startsWith("proj:") ? id.slice(5) : undefined;
  if (projectId) {
    try {
      localStorage.removeItem(projectChatStorageKey(projectId));
    } catch {
      /* ignore */
    }
  }
  return true;
}

export function chatIdForProject(projectId: string) {
  return `proj:${projectId}`;
}

export { GENERAL_ID };
